import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IMPORT_MAX_ROWS,
  commitImport,
  testImport,
  validateImport,
  type ImportCommitResult,
  type ImportPlanRow,
  type ImportRow,
  type ImportTestResult,
  type ImportValidation,
} from '../lib/api';
import { KOLOM_CSV, barisKeCsv, contohCsv, uraiCsv } from '../lib/csvKamera';
import { useBahasa } from '../i18n';

/**
 * Bulk Import (§G11.3) — lima langkah berurutan, dan **bisa mundur** tanpa
 * kehilangan isian.
 *
 * *"Seandainya ada 10 kamera ditambah, kita bisa menambahkan banyak kamera
 * melalui template, tabel yang bisa di-+ + +, atau malah CSV. Proses: Upload →
 * Validasi → Preview → Test → Import."*
 *
 * **Dua pintu masuk, satu jalur.** Unggah CSV dan tabel yang bisa ditambah
 * baris menghasilkan bentuk data yang sama persis, lalu keduanya masuk ke
 * validator yang sama — di server. Dua jalur validasi berarti CSV dan tabel
 * cepat atau lambat punya pendapat berbeda tentang baris yang sama.
 */

type Langkah = 'unggah' | 'validasi' | 'pratinjau' | 'uji' | 'impor';

const LANGKAH: { id: Langkah; label: string }[] = [
  { id: 'unggah', label: 'Upload' },
  { id: 'validasi', label: 'Validate' },
  { id: 'pratinjau', label: 'Preview' },
  { id: 'uji', label: 'Test' },
  { id: 'impor', label: 'Import' },
];

/** Kolom yang muncul di editor. Sisanya jarang dipakai dan tetap terbawa dari CSV. */
const KOLOM_EDITOR = [
  'name',
  'host',
  'port',
  'transport',
  'mainPath',
  'subPath',
  'username',
  'password',
  'maxDay',
  'template',
  'tags',
] as const;

const barisKosong = (): ImportRow => ({ name: '', host: '' });

function unduh(namaBerkas: string, isi: string) {
  const url = URL.createObjectURL(new Blob([isi], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = namaBerkas;
  a.click();
  URL.revokeObjectURL(url);
}

export function CameraImportPage() {
  const { t } = useBahasa();
  const navigate = useNavigate();

  const [langkah, setLangkah] = useState<Langkah>('unggah');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [kolomAsing, setKolomAsing] = useState<string[]>([]);
  const [validasi, setValidasi] = useState<ImportValidation | null>(null);
  const [uji, setUji] = useState<{ results: ImportTestResult[]; skipped: number[] } | null>(null);
  const [hasilImpor, setHasilImpor] = useState<ImportCommitResult | null>(null);
  const [sibuk, setSibuk] = useState(false);
  const [galat, setGalat] = useState<string | null>(null);

  const rencanaOk = useMemo(
    () => (validasi ? validasi.rows.filter((r) => r.ok) : []),
    [validasi],
  );
  const barisOk = useMemo(
    () => rencanaOk.map((r) => rows[r.index]).filter((r): r is ImportRow => r !== undefined),
    [rencanaOk, rows],
  );

  function set(i: number, kunci: string, nilai: string) {
    setRows((cur) =>
      cur.map((r, n) => {
        if (n !== i) return r;
        const next: Record<string, unknown> = { ...r };
        if (kunci === 'tags') {
          next.tags = nilai.split(/[,;]/).map((s) => s.trim()).filter((s) => s !== '');
        } else if (kunci === 'port' || kunci === 'maxDay') {
          next[kunci] = nilai.trim() === '' ? null : Number(nilai);
        } else {
          next[kunci] = nilai === '' ? null : nilai;
        }
        // `name` dan `host` tidak pernah null — validator server menolak string
        // kosong dengan kalimat yang menyebut barisnya, dan itu yang mau dibaca
        // orangnya, bukan `null` yang gagal di tempat lain.
        if (kunci === 'name' || kunci === 'host') next[kunci] = nilai;
        return next as unknown as ImportRow;
      }),
    );
  }

  async function periksa() {
    setSibuk(true);
    setGalat(null);
    try {
      setValidasi(await validateImport(rows));
      setLangkah('validasi');
    } catch (e) {
      setGalat((e as Error).message);
    } finally {
      setSibuk(false);
    }
  }

  async function jalankanUji() {
    setSibuk(true);
    setGalat(null);
    try {
      setUji(await testImport(barisOk));
    } catch (e) {
      setGalat((e as Error).message);
    } finally {
      setSibuk(false);
    }
  }

  async function impor() {
    setSibuk(true);
    setGalat(null);
    try {
      setHasilImpor(await commitImport(barisOk));
      setLangkah('impor');
    } catch (e) {
      setGalat((e as Error).message);
    } finally {
      setSibuk(false);
    }
  }

  const issueBaris = (i: number): ImportPlanRow | undefined =>
    validasi?.rows.find((r) => r.index === i);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <h1 className="text-xl">{t('Import cameras')}</h1>

      {/* Penunjuk langkah. Tidak bisa diklik untuk melompat ke depan — langkah
          berikutnya butuh hasil langkah sebelumnya — tapi tombol "Back" di tiap
          layar mengembalikan tanpa kehilangan isian (§G11.3). */}
      <ol className="mt-4 flex flex-wrap gap-2 text-sm">
        {LANGKAH.map((s, i) => {
          const aktif = s.id === langkah;
          const lewat = LANGKAH.findIndex((x) => x.id === langkah) > i;
          return (
            <li
              key={s.id}
              aria-current={aktif ? 'step' : undefined}
              className={`rounded-md border px-3 py-1 ${
                aktif
                  ? 'border-accent text-accent-text'
                  : lewat
                    ? 'border-line text-muted'
                    : 'border-line text-muted opacity-60'
              }`}
            >
              {i + 1}. {t(s.label)}
            </li>
          );
        })}
      </ol>

      {galat && (
        <p role="alert" className="mt-4 rounded-md border border-red-600 px-3 py-2 text-sm">
          {galat}
        </p>
      )}

      {langkah === 'unggah' && (
        <section className="mt-6">
          <p className="text-sm text-muted">
            {t('Up to {n} cameras at a time. The file is read in your browser — it is never uploaded.', {
              n: IMPORT_MAX_ROWS,
            })}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="min-h-tap cursor-pointer rounded-md border border-line px-4 py-2 text-sm">
              {t('Choose a CSV file')}
              <input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const hasil = uraiCsv(await f.text());
                  if (hasil.galat) {
                    setGalat(hasil.galat);
                    return;
                  }
                  if (hasil.rows.length > IMPORT_MAX_ROWS) {
                    setGalat(
                      t('That file has {n} rows — the limit is {max}.', {
                        n: hasil.rows.length,
                        max: IMPORT_MAX_ROWS,
                      }),
                    );
                    return;
                  }
                  setGalat(null);
                  setKolomAsing(hasil.kolomTidakDikenal);
                  setRows(hasil.rows);
                  setValidasi(null);
                  setUji(null);
                }}
              />
            </label>

            <button
              type="button"
              onClick={() => unduh('lensa-import-contoh.csv', contohCsv())}
              className="min-h-tap rounded-md border border-line px-4 text-sm"
            >
              {t('Download a sample file')}
            </button>

            <button
              type="button"
              onClick={() => {
                setRows([barisKosong()]);
                setValidasi(null);
                setUji(null);
                setGalat(null);
              }}
              className="min-h-tap rounded-md border border-line px-4 text-sm"
            >
              {t('Start from an empty table instead')}
            </button>
          </div>

          {/* Kolom asing disebutkan, bukan dibuang diam-diam: kolom yang salah
              ketik satu huruf akan hilang tanpa jejak, dan yang menemukannya
              adalah orang yang bertanya kenapa retensinya tidak ikut. */}
          {kolomAsing.length > 0 && (
            <p className="mt-3 rounded-md border border-amber-500 px-3 py-2 text-xs">
              {t('These columns were not recognised and will be ignored: {daftar}', {
                daftar: kolomAsing.join(', '),
              })}
            </p>
          )}

          <p className="mt-4 rounded-md border border-amber-500 px-3 py-2 text-xs">
            {t('A CSV holding camera passwords is a file that gets left in someone’s Downloads folder. Put the credentials in a template instead and name it in the “template” column.')}
          </p>

          {rows.length > 0 && (
            <button
              type="button"
              disabled={sibuk}
              onClick={() => void periksa()}
              className="mt-4 min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast"
            >
              {sibuk ? t('Checking…') : t('Check {n} rows', { n: rows.length })}
            </button>
          )}
        </section>
      )}

      {(langkah === 'validasi' || langkah === 'pratinjau') && validasi && (
        <section className="mt-6">
          <p className="text-sm">
            {t('{ok} rows are ready, {bad} need fixing.', {
              ok: validasi.okCount,
              bad: validasi.errorCount,
            })}
          </p>

          {langkah === 'validasi' ? (
            <>
              {/* Baris bermasalah diperbaiki DI TEMPAT, di tabel yang sama —
                  berkas yang ditolak seluruhnya memaksa orang mengulang dari
                  Excel, dan itu jenis kegagalan yang membuat fiturnya tidak
                  dipakai. */}
              <div className="mt-3 overflow-x-auto rounded-lg border border-line">
                <table className="w-full text-xs">
                  <thead className="bg-subtle text-left">
                    <tr>
                      <th className="p-2">#</th>
                      {KOLOM_EDITOR.map((k) => (
                        <th key={k} className="p-2 font-normal">
                          {k}
                        </th>
                      ))}
                      <th className="p-2 font-normal">{t('Issues')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const plan = issueBaris(i);
                      const rusak = plan !== undefined && !plan.ok;
                      return (
                        <tr
                          key={i}
                          className={`border-t border-line ${rusak ? 'bg-red-600/5' : ''}`}
                        >
                          <td className="p-1 text-muted">{i + 1}</td>
                          {KOLOM_EDITOR.map((k) => {
                            const nilai = (r as unknown as Record<string, unknown>)[k];
                            const teks = Array.isArray(nilai)
                              ? nilai.join(';')
                              : nilai === null || nilai === undefined
                                ? ''
                                : String(nilai);
                            const salahDiSini = plan?.issues.some(
                              (x) => x.field === k && x.severity === 'error',
                            );
                            return (
                              <td key={k} className="p-1">
                                <input
                                  value={teks}
                                  type={k === 'password' ? 'password' : 'text'}
                                  onChange={(e) => set(i, k, e.target.value)}
                                  aria-label={`${k} ${t('row')} ${i + 1}`}
                                  className={`min-h-tap w-28 rounded border bg-page px-1 ${
                                    salahDiSini ? 'border-red-600' : 'border-line'
                                  }`}
                                />
                              </td>
                            );
                          })}
                          <td className="p-1">
                            <ul>
                              {plan?.issues.map((x, n) => (
                                <li
                                  key={n}
                                  className={
                                    x.severity === 'error' ? 'text-red-600' : 'text-amber-600'
                                  }
                                >
                                  {x.message}
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setRows([...rows, barisKosong()])}
                  disabled={rows.length >= IMPORT_MAX_ROWS}
                  className="min-h-tap rounded-md border border-line px-4 text-sm"
                >
                  {t('+ Add a row')}
                </button>
                <button
                  type="button"
                  disabled={sibuk}
                  onClick={() => void periksa()}
                  className="min-h-tap rounded-md border border-line px-4 text-sm"
                >
                  {sibuk ? t('Checking…') : t('Check again')}
                </button>
                <button
                  type="button"
                  disabled={validasi.okCount === 0}
                  onClick={() => setLangkah('pratinjau')}
                  className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast disabled:opacity-50"
                >
                  {t('Preview {n} cameras', { n: validasi.okCount })}
                </button>
                <button
                  type="button"
                  onClick={() => setLangkah('unggah')}
                  className="min-h-tap rounded-md border border-line px-4 text-sm"
                >
                  {t('Back')}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Pratinjau menampilkan kamera JADI, bukan baris CSV — termasuk
                  `folderName` yang akan dibuat: nilai turunan yang menentukan ke
                  mana rekamannya ditulis, dan satu-satunya yang tidak bisa
                  diubah sesudahnya tanpa memindahkan folder. */}
              <div className="mt-3 overflow-x-auto rounded-lg border border-line">
                <table className="w-full text-sm">
                  <thead className="bg-subtle text-left">
                    <tr>
                      <th className="p-2 font-normal">{t('Name')}</th>
                      <th className="p-2 font-normal">{t('Host')}</th>
                      <th className="p-2 font-normal">{t('Main stream path')}</th>
                      <th className="p-2 font-normal">{t('Recording folder')}</th>
                      <th className="p-2 font-normal">{t('Retention')}</th>
                      <th className="p-2 font-normal">{t('Tags')}</th>
                      <th className="p-2 font-normal">{t('Password')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rencanaOk.map((r) => (
                      <tr key={r.index} className="border-t border-line">
                        <td className="p-2">{r.name}</td>
                        <td className="p-2 font-mono text-xs">
                          {r.host}:{r.port} {r.transport.toUpperCase()}
                        </td>
                        <td className="p-2 font-mono text-xs">{r.mainPath}</td>
                        <td className="p-2 font-mono text-xs">{r.folderName}</td>
                        <td className="p-2">
                          {r.recordEnabled
                            ? r.maxDay === null
                              ? t('global')
                              : `${r.maxDay}d`
                            : t('View Only')}
                        </td>
                        <td className="p-2">{r.tagCount}</td>
                        <td className="p-2">{r.hasPassword ? t('set') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {validasi.errorCount > 0 && (
                <p className="mt-3 text-xs text-muted">
                  {t('{n} rows are excluded because they still have errors.', {
                    n: validasi.errorCount,
                  })}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setLangkah('uji')}
                  className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast"
                >
                  {t('Next: test the connections')}
                </button>
                <button
                  type="button"
                  onClick={() => setLangkah('validasi')}
                  className="min-h-tap rounded-md border border-line px-4 text-sm"
                >
                  {t('Back')}
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {langkah === 'uji' && validasi && (
        <section className="mt-6">
          {/* Uji BOLEH dilewati, dan itu disengaja: kamera sering didaftarkan
              sebelum kabelnya terpasang, dan impor yang menolak melanjutkan
              sampai semuanya menyahut memaksa operator menunggu tukang. */}
          <p className="text-sm text-muted">
            {t('Optional. Cameras are often registered before the cabling is finished — you can import without testing.')}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={sibuk}
              onClick={() => void jalankanUji()}
              className="min-h-tap rounded-md border border-line px-4 text-sm"
            >
              {sibuk ? t('Testing…') : t('Test {n} cameras', { n: rencanaOk.length })}
            </button>
            <button
              type="button"
              disabled={sibuk}
              onClick={() => void impor()}
              className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast"
            >
              {t('Import {n} cameras', { n: rencanaOk.length })}
            </button>
            <button
              type="button"
              onClick={() => setLangkah('pratinjau')}
              className="min-h-tap rounded-md border border-line px-4 text-sm"
            >
              {t('Back')}
            </button>
          </div>

          {uji && (
            <ul className="mt-4 divide-y divide-line border-y border-line text-sm">
              {uji.results.map((r) => (
                <li key={r.index} className="flex gap-2 py-1.5">
                  <span className={r.ok ? 'text-green-600' : 'text-red-600'}>
                    {r.ok ? '✓' : '✗'}
                  </span>
                  <span className="font-medium">{r.name}</span>
                  {r.message && <span className="text-muted">{r.message}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {langkah === 'impor' && hasilImpor && (
        <section className="mt-6">
          <p className="text-base">
            {t('{n} cameras created, {bad} failed.', {
              n: hasilImpor.createdCount,
              bad: hasilImpor.failedCount,
            })}
          </p>

          {hasilImpor.failedCount > 0 && (
            <>
              <ul className="mt-3 divide-y divide-line border-y border-line text-sm">
                {hasilImpor.results
                  .filter((r) => !r.ok)
                  .map((r) => (
                    <li key={r.index} className="py-1.5">
                      <span className="font-medium">{r.name}</span>
                      {r.message ? ` — ${r.message}` : ''}
                    </li>
                  ))}
              </ul>
              {/* Baris yang gagal dikembalikan sebagai CSV, supaya diperbaiki di
                  Excel lalu diunggah lagi — bukan diketik ulang dari layar. */}
              <button
                type="button"
                onClick={() =>
                  unduh(
                    'lensa-import-gagal.csv',
                    barisKeCsv(
                      hasilImpor.results
                        .filter((r) => !r.ok)
                        .map((r) => rows[r.index])
                        .filter((r): r is ImportRow => r !== undefined),
                    ),
                  )
                }
                className="mt-3 min-h-tap rounded-md border border-line px-4 text-sm"
              >
                {t('Download the failed rows as CSV')}
              </button>
            </>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate('/cameras')}
              className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast"
            >
              {t('Go to Cameras')}
            </button>
            <button
              type="button"
              onClick={() => {
                setRows([]);
                setValidasi(null);
                setUji(null);
                setHasilImpor(null);
                setKolomAsing([]);
                setLangkah('unggah');
              }}
              className="min-h-tap rounded-md border border-line px-4 text-sm"
            >
              {t('Import another file')}
            </button>
          </div>
        </section>
      )}

      <p className="mt-8 text-xs text-muted">
        {t('Recognised columns: {daftar}', { daftar: KOLOM_CSV.join(', ') })}
      </p>
    </div>
  );
}
